import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useI18n } from "../../i18n/useI18n.js";
import { RootState } from "../../../app/store.js";
import { Loader } from "./Loader.js";
import Drawer from "@/components/UI/Drawer/Drawer.js";
import formStyles from '../styles/userForm.module.scss';
import { RolData } from "../slices/rolSlice.js";
import { useGetAllSeniority } from "../hooks/useGetAllSeniority.js";
import { useGetAllCompanies } from "../hooks/useGetAllCompanies";
import { useUpdateUser } from "../hooks/useUpdateUser.js";
import { useCreateUser } from "../hooks/useCreateUser.js";
import { TeamData } from "../slices/teamSlice.js";
import { IUsersData } from "../slices/usersSlice.js";
import { SeniorityData } from "../slices/senioritySlice.js";
import { IFormData } from "../../../api/users/types/index.js";
import { emptyUser } from "../constants/index.js";
import { ErrorMessage } from "../../../components/Notification/ErrorMessage.js";
import { ICompany } from "@/features/companies/slices/companiesSlice.js";
import { useGetAllTeams } from "../hooks/useGetAllTeams.js";
import { getItem } from "@/features/authentication/utils/handleLocalStorage.js";
import { Button } from "@/components/UI/index.js";

interface UserFormDrawerProps {
  dataUser?: IUsersData;
  isOpen: boolean;
  onClose: (data?: IUsersData) => void;
  refetchUsers: () => void;
}

const UserFormDrawer = ({ dataUser, isOpen, onClose, refetchUsers }: UserFormDrawerProps) => {
  const { t } = useI18n();
  const token = getItem("token");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Algo salio mal Intentalo nuevamente");
  const { data: teams } = useGetAllTeams({ enabled: !!token });
  const { roles } = useSelector((state: RootState) => state.roles);
  const { data: companiesData } = useGetAllCompanies();
  const { data: seniority } = useGetAllSeniority();
  const { mutateAsync: mutateCreateUser, isPending: isPendingCreate } = useCreateUser();
  const { mutateAsync: mutateUpdateUser, isPending: isPendingUpdate } = useUpdateUser();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<IFormData>({
    defaultValues: emptyUser,
  });

  useEffect(() => {
    if (dataUser && dataUser.userid) {
      const a = {
        ...dataUser,
        username: dataUser.username ?? '',
        role: dataUser.role ?? '',
        email: dataUser.email ?? '',
        firstName: dataUser.firstname ?? '',
        secondName: dataUser.secondname ?? '',
        firstLastname: dataUser.firstlastname ?? '',
        secondLastname: dataUser.secondlastname ?? '',
        telephone: dataUser.telephone ?? '',
        address: dataUser.address ?? '',
        birthday: dataUser.birthday ? new Date(dataUser.birthday).toISOString().split("T")[0] : "",
        idSeniority: getSeniorityId(dataUser.seniority, seniority),
        idCompany: dataUser.idcompany ? Number(dataUser.idcompany) : 0,
      }
      console.log(a)
      reset(a);
    } else {
      reset(emptyUser);
    }
  }, [dataUser, isOpen, reset, seniority]);


  const getSeniorityId = (
    seniorityDesc: string | null,
    seniorityList?: { data: SeniorityData[] }
  ) => {
    if (!seniorityDesc || !seniorityList?.data) return 0;
    const found = seniorityList.data.find(
      (s: SeniorityData) => s.description === seniorityDesc
    );
    return found ? found.id : 0;
  };

  const onSubmit = async (data: IFormData) => {
    const formData = {
      ...data,
      idArea: Number(data.idArea),
      idCompany: Number(data.idCompany),
      idSeniority: Number(data.idSeniority),
    };

    try {
      if (dataUser && dataUser.userid) {
        await mutateUpdateUser({ formData, userName: dataUser.username }, {
          onSuccess: handleSuccess,
          onError: (err) => handleError(err),
        });
      } else {
        await mutateCreateUser(formData, {
          onSuccess: handleSuccess,
          onError: (err) => handleError(err),
        });
      }
    } catch (err) {
      handleError(err as Error);
    }
  };

  const handleSuccess = () => {
    refetchUsers();
    onClose();
  };

  const handleError = (error: Error) => {
    const email = watch("email");
    setShowErrorMessage(true);
    setErrorMessage(error?.message || `El usuario ${email} ya está registrado`);
  };

  const handleClose = () => {
    setShowErrorMessage(false);
    setErrorMessage("");
    reset(emptyUser);
    onClose();
  };

  const validateBirthdate = (value: string) => {
    if (!value) return true;
    const selectedDate = new Date(value);
    const today = new Date();
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return selectedDate <= today || "La fecha de nacimiento no puede ser mayor a hoy";
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Drawer.Header onClose={handleClose}>
          {dataUser && dataUser.userid
            ? t("module_users.modal_update_title")
            : t("module_users.modal_create_title")}
        </Drawer.Header>

        <Drawer.Body>
          {(isPendingCreate || isPendingUpdate) && <Loader />}
          <div className={formStyles.formGrid}>
            <div className={formStyles.formField}>
              <label htmlFor="firstName" className={formStyles.label}>{t("module_users.modal_create_phld_firstName")}</label>
              <input id="firstName" className={formStyles.input} {...register("firstName", { required: true })} />
              {errors["firstName"] && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error1")}</p>}
            </div>

            <div className={formStyles.formField}>
              <label htmlFor="secondName" className={formStyles.label}>{t("module_users.modal_create_user_phld_secondName")}</label>
              <input id="secondName" className={formStyles.input} {...register("secondName")} />
            </div>

            <div className={formStyles.formField}>
              <label htmlFor="firstLastname" className={formStyles.label}>{t("module_users.modal_create_user_phld_first_LastName")}</label>
              <input id="firstLastname" className={formStyles.input} {...register("firstLastname", { required: true })} />
              {errors["firstLastname"] && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error_firstLastName")}</p>}
            </div>

            <div className={formStyles.formField}>
              <label htmlFor="secondLastname" className={formStyles.label}>{t("module_users.modal_create_user_phld_second_LastName")}</label>
              <input id="secondLastname" className={formStyles.input} {...register("secondLastname")} />
            </div>

            <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
              <label htmlFor="username" className={formStyles.label}>{t("module_users.modal_create_user_phld_username")}</label>
              <input id="username" className={formStyles.input} {...register("username", { required: true })} />
              {errors["username"] && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error_username")}</p>}
            </div>

            <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
              <label className={formStyles.label}>{t("module_users.modal_create_label2")}</label>
              <input type="email" className={formStyles.input} {...register("email", { required: t("module_users.modal_create_error2") })} />
              {errors["email"] && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error2")}</p>}
            </div>

            <div className={formStyles.formField}>
              <label className={formStyles.label}>{t("module_users.modal_create_user_phld_telephone")}</label>
              <input type="number" className={formStyles.input} {...register("telephone", { required: true })} />
              {errors["telephone"] && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error_telephone")}</p>}
            </div>

            <div className={formStyles.formField}>
              <label className={formStyles.label}>{t("module_users.modal_create_user_phld_address")}</label>
              <input className={formStyles.input} {...register("address", { required: true })} />
              {errors["address"] && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error_address")}</p>}
            </div>

            <div className={formStyles.formField}>
              <label className={formStyles.label}>{t("module_users.modal_create_user_birthdate")}</label>
              <input type="date" className={formStyles.input} {...register("birthday", { required: t("module_users.modal_create_error_birthdate"), validate: validateBirthdate })} />
              {errors["birthday"] && <p className={formStyles.errorMessage}>{errors["birthday"].message}</p>}
            </div>

            <div className={formStyles.formField}>
              <label className={formStyles.label}>{t("module_users.modal_create_label4")}</label>
              <select data-testid="select-team" className={formStyles.select} {...register("idArea", { required: true })}>
                <option value="" disabled>{t("module_users.modal_create_default_select")}</option>
                {teams?.data?.map((team: TeamData) => (
                  <option value={team.id} key={team.id}>{team.areaName}</option>
                ))}
              </select>
              {errors['idArea'] && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error4")}</p>}
            </div>

            <div className={formStyles.formField}>
              <label className={formStyles.label}>{t("module_users.modal_create_label3")}</label>
              <select data-testid="select-profile" className={formStyles.select} {...register("role", { required: true })}>
                <option value="" disabled>{t("module_users.modal_create_default_select")}</option>
                {roles?.map((rol: RolData, index: number) => (
                  <option value={rol.name} key={index}>{rol.name}</option>
                ))}
              </select>
              {errors?.role && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error3")}</p>}
            </div>

            <div className={formStyles.formField}>
              <label className={formStyles.label}>{t("module_users.modal_create_label5")}</label>
              <select data-testid="select-seniority" className={formStyles.select} {...register("idSeniority", { required: t("module_users.modal_create_error5") })}>
                <option value="" disabled>{t("module_users.modal_create_default_select")}</option>
                {seniority?.data?.map((s: SeniorityData) => (
                  <option value={s.id} key={s.id}>{s.description}</option>
                ))}
              </select>
              {errors?.idSeniority && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error5")}</p>}
            </div>

            <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
              <label className={formStyles.label}>{t("module_users.modal_create_user_company")}</label>
              <select data-testid="select-idCompany" className={formStyles.select} {...register("idCompany", { required: t("module_users.modal_create_error_company") })}>
                <option value="" disabled>{t("module_users.modal_create_default_select")}</option>
                {companiesData?.data?.map((company: ICompany) => (
                  <option value={company.id} key={company.id}>{company.nameCompany}</option>
                ))}
              </select>
              {errors?.idCompany && <p className={formStyles.errorMessage}>{t("module_users.modal_create_error_company")}</p>}
            </div>

            {showErrorMessage && <ErrorMessage message={errorMessage} />}
          </div>
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={handleClose}>{t("module_users.modal_create_button_cancel")}</Button>
          <Button variant="primary" type="submit" data-testid="button-create-new-user">
            {dataUser && dataUser.userid
              ? t("module_users.modal_update_button_submit")
              : t("module_users.modal_create_button_submit")}
          </Button>
        </Drawer.Footer>
      </form>
    </Drawer>
  );
};

export default UserFormDrawer; 